import { createDealerAxios } from '../factories/axios-factory';
import { useMapProposal } from '../functions';
import { useAuthStore } from '../stores/auth-store';
import { ApplicationNote, AvailableProposal, LoanApplication, CreditCheck, PayoutPackage } from 'src/types/loan-application/index';
import { QuoteSendRequest, QuoteProceed, Residuals, ResidualsRequest, QuickQuote } from 'src/types/quote/index';
import { MultiDealerApplicationRequest } from 'src/types/dealer';
import ResidualResponse from 'src/types/quote/residual-response';

const backendUser = createDealerAxios(process.env.DealersApiUrl!);

let ws = null as null | WebSocket;

const proposalService = {
  getApplications(aid: number, days: number) {
    return new Promise<LoanApplication[]>((resolve, reject) => {
      backendUser.get(
        `/application/${aid}?days=${days > 0 ? days : 30}&includeProposals=true&expandPrime=false&excludedStates=10&excludedStates=4&excludedStates=5&excludedStates=6&excludedStates=7&excludedStates=8`
      ).then(res => {
        if (res.status === 200) {
          let d = res.data as LoanApplication[];
          d.forEach(a => {
            a.proposals ? a.proposals = useMapProposal(a.proposals) : null
          });

          resolve(d);
          return;
        }
        resolve([]);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getMultiDealerApplications(request: MultiDealerApplicationRequest) {
    return new Promise<LoanApplication[]>((resolve, reject) => {
      backendUser.post(
        `/applications`,
        request
      ).then(res => {
        if (res.status === 200) {
          let d = res.data as LoanApplication[];

          d.forEach(a => {
            a.proposals ? a.proposals = useMapProposal(a.proposals) : null;
            if(a.createdBy == "Customer (Web)" &&  (!a.vehicle || !a.vehicle.vehicleId)){
              a.vehicle.make = "Eligibility"
              a.vehicle.model = "Check"
              a.vehicle.price = a.loanAmount ?? 10000;
              a.vehicle.vehicleDateOfFirstReg = new Date()
              a.vehicle.mileage = 10000;
              a.vehicle.vehicleRegistration = "N/A"
            }
            if(a.vehicle.vrm && !a.vehicle.vehicleRegistration){
              a.vehicle.vehicleRegistration = a.vehicle.vrm
            }
          });

          resolve(d);

          return;
        }
        resolve([]);
      })
        .catch(error => {
          reject(error);
        })
    })
  },

  getApplication(brokerReference: number) {
    return new Promise<LoanApplication>((resolve, reject) => {
      backendUser.get(
        `/application-single/${brokerReference}`
      ).then(res => {
        if (res.status === 200) {
          let d = res.data as LoanApplication;
          resolve(d);
          return;
        }
        reject(null);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getPayoutPackage(applicationId: number, aid: string) {
    return new Promise<PayoutPackage>((resolve, reject) => {
      backendUser.get(
        `/payout/${applicationId}`,
        {
          headers: {
            affiliateUserId: Number(aid)
          }
        }
      ).then(response => {
        let p = response.data as PayoutPackage;
        if(p.customerRequirements && p.customerRequirements.length > 0) {
          p.customerRequirements = p.customerRequirements.filter(c => c.requirementTypeId !== 167);
        }
        if(p.customerMiscRequirements && p.customerMiscRequirements.length > 0) {
          p.customerMiscRequirements = p.customerMiscRequirements.filter(c => c.requirementTypeId !== 167);
        }
        resolve(p);
      }).catch(error => {
        reject(error)
      })
    })
  },
  createApplication(proposal: LoanApplication) {

    proposal.isV2App = false;
    if (
      window.location.hostname.includes("kind-glacier") ||
      window.location.hostname.includes("thankful-plant") ||
      window.location.hostname.includes("dealerv2")
    ) {
      proposal.isV2App = true;
    }

    return new Promise<string>((resolve, reject) => {
      backendUser.post(
        `/application`,
        proposal
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data.id as string);
          return;
        }
        reject();
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getAvailableProposals(applicationId: number, expand: boolean) {
    return new Promise<AvailableProposal[]>((resolve, reject) => {
      backendUser.get(
        `/quote/proposals-expanded?applicationId=${applicationId}&expandedProposals=${expand}`
      ).then(res => {
        const mapped = useMapProposal(res.data)
          .filter(p => p.financeCompanyId !== 62); //remove mann island props

        // Dealer portal only Alphera min lend amount is 3000
        mapped.forEach(m => {
          if (m.financeCompanyId === 50 && m.minLendAmount === 12500) {
            m.minLendAmount = 3000;
            m.minLendAmountPcp = 3000;
          }
        })
        resolve(mapped);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getCreditCheck(applicationId: number) {
    return new Promise<CreditCheck>((resolve, reject) => {
      backendUser.get(
        `/credit-check/${applicationId}`
      ).then(res => {
        resolve(res.data as CreditCheck)
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getResiduals(req: ResidualsRequest) {
    return new Promise<ResidualResponse[] | null>((resolve, reject) => {
      backendUser.post(`/v2/vehicle/residuals/multiple`, req).then(res => {
        if (res.status === 204) {
          resolve(null);
        }
        resolve(res.data as ResidualResponse[]);
      }).catch(error => {
        resolve(null);
      })
    })
  },
  proceedWithQuote(req: QuoteProceed) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/quote/proceed`,
        req
      ).then(res => {
        resolve(true);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  sendQuoteToCustomer(req: QuoteSendRequest) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/quote/send`,
        req
      ).then(res => {
        resolve(true);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  sendCustomerEmail(req: QuoteProceed) {
    return new Promise<boolean>((resolve, reject) => {
      backendUser.post(
        `/quote/customer/proceed`,
        req
      ).then(res => {
        resolve(true);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getNotes(brokerReference?: number, cosmosId?: string) {
    return new Promise<ApplicationNote[]>((resolve, reject) => {
      backendUser.get(
        `/application-notes?${brokerReference && brokerReference > 0 ? 'brokerReference=' + brokerReference : 'cosmoId=' + cosmosId}`
      ).then(res => {
        resolve(res.data);
      })
        .catch(error => {
          console.log("gne:", error)
          resolve([]);
        })
    })
  },
  addNote(note: ApplicationNote) {
    return new Promise<boolean>((resolve, reject) => {
      const form = new FormData();
      form.append("noteText", note.text);
      form.append("createdBy", note.createdBy);
      form.append("affiliateUserId", note.affiliateUserId);
      backendUser.post(
        `/application/notes?${note.brokerReference && note.brokerReference > 0 ? 'brokerReference=' + note.brokerReference : 'cosmoId=' + note.cosmosId}`,
        form,
        {
          headers: { "Content-Type": `multipart/form-data` }
        },
      ).then(res => {
        if (res.status === 200) {
          resolve(true);
          return;
        }
        resolve(false);
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  createQuickQuote(quote: QuickQuote) {
    //@ts-ignore
    quote.affiliateUserId = quote.affiliateUserId.toString();

    return new Promise<string>((resolve, reject) => {
      backendUser.post(
        `/quick-quote`,
        quote
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data as string);
          return;
        }
        reject();
      })
        .catch(error => {
          reject(error);
        })
    })
  },
  getAllQuickQuotes() {
    return new Promise<QuickQuote[]>((resolve, reject) => {
      backendUser.get(
        `/quick-quotes/${useAuthStore().affiliateUserId}`,
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data as QuickQuote[]);
          return;
        }
        if (res.status === 204) {
          resolve([]);
          return;
        }
        reject();
      })
        .catch(error => {
          resolve([]);
        })
    })
  },
  getMultiDealerQuickQuotes(aids: string[], days: number) {
    return new Promise<QuickQuote[]>((resolve, reject) => {
      backendUser.post(
        `/quick-quotes`,
        {
          affiliateUserIds: aids,
          days
        }
      ).then(res => {
        if (res.status === 200) {
          resolve(res.data as QuickQuote[]);
          return;
        }
        if (res.status === 204) {
          resolve([]);
          return;
        }
        reject();
      })
        .catch(error => {
          console.log("mdqqe:", error)
          resolve([]);
        })
    })
  },
  subscribeGlobal() {
    if (ws) {
      ws.close();
    }
    return new Promise<string>((resolve, reject) => {
      backendUser.get(`/subscribe-global`)
        .then((response) => {
          if (response.status === 200) {
            resolve(response.data.url as string);
          }
          reject(null);
        })
        .catch((error) => {
          console.log("ps error: ", error);
          reject(error);
        });
    });
  }
};

export default proposalService;


